/**
 * Canonical exchange event / custody identity helpers.
 * Backend and frontend MUST use the same functions — no parallel ad-hoc IDs.
 *
 * Event ID format:  ex:<vendor>:<accountId>:<txRef>[:suffix]
 * Fee ID format:    ex:<vendor>:<accountId>:<txRef>:fee
 * Custody format:   exchange:<vendor>:<accountId>
 */

export function normalizeExchangeAccountId(value) {
  const raw = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9._-]/g, '');

  return raw || 'default';
}

/** Vendor only (bitpanda). Strips leading exchange: and ignores account segment. */
export function normalizeExchangeVendorName(value) {
  let raw = String(value || '').trim().toLowerCase();
  while (raw.startsWith('exchange:')) {
    raw = raw.slice('exchange:'.length).trim();
  }
  if (!raw) return 'unknown';
  const first = raw.split(':').filter(Boolean)[0];
  return first || 'unknown';
}

export function pickExchangeAccountId(row = {}, accountIdHint = null) {
  return normalizeExchangeAccountId(
    accountIdHint ??
      row.exchange_account_id ??
      row.exchangeAccountId ??
      row.account_id ??
      row.accountId ??
      row.account ??
      row.uid
  );
}

export function pickExchangeVendor(row = {}) {
  return normalizeExchangeVendorName(
    row.exchange_source ??
      row.exchangeSource ??
      row.source ??
      row.exchange ??
      'unknown'
  );
}

function pickExchangeTxRef(row = {}, timestampFallback = null) {
  const ref =
    row.txid ??
    row.txId ??
    row.id ??
    row.order_id ??
    row.orderId ??
    row.transfer_id ??
    row.transferId ??
    row.refId ??
    row.refid ??
    timestampFallback ??
    row.ts ??
    row.timestamp ??
    '';
  return String(ref);
}

/**
 * Canonical exchange event id used for FIFO matching, tax rows, UI joins, exports.
 * Format: ex:<vendor>:<accountId>:<txRef>
 */
export function buildExchangeEventId(row = {}, timestampFallback = null, suffix = null) {
  const vendor = pickExchangeVendor(row);
  const accountId = pickExchangeAccountId(row);
  const txRef = pickExchangeTxRef(row, timestampFallback);
  const base = `ex:${vendor}:${accountId}:${txRef}`;
  if (suffix == null || suffix === '') return base;
  return `${base}:${String(suffix).replace(/^:/, '')}`;
}

export function buildExchangeFeeEventId(row = {}, timestampFallback = null) {
  return `${buildExchangeEventId(row, timestampFallback)}:fee`;
}

/** exchange:<vendor>:<accountId> — never bare exchange:<vendor>. */
export function buildExchangeCustodyLocationId(row = {}, accountIdHint = null) {
  if (typeof row === 'string') {
    const vendor = normalizeExchangeVendorName(row);
    const accountId = normalizeExchangeAccountId(accountIdHint);
    return `exchange:${vendor}:${accountId}`;
  }
  const vendor = pickExchangeVendor(row);
  const accountId = pickExchangeAccountId(row, accountIdHint);
  return `exchange:${vendor}:${accountId}`;
}

/**
 * True for pre-account-scope IDs: ex:<vendor>:<txRef> or ex:<vendor>:<txRef>:fee
 * (exactly one segment between vendor and optional fee).
 */
export function isLegacyExchangeEventId(eventId) {
  const raw = String(eventId || '').trim().toLowerCase();
  if (!raw.startsWith('ex:')) return false;
  const parts = raw.slice(3).split(':').filter(Boolean);
  if (parts.length === 2) return true;
  if (parts.length === 3 && parts[2] === 'fee') return true;
  return false;
}

function stablePart(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return String(value).trim();
}

/**
 * Full row fingerprint for dedupe across frontend + backend.
 * Includes account so two identical fills on two accounts never collide.
 */
export function buildExchangeRowFingerprint(row = {}) {
  const vendor = pickExchangeVendor(row);
  const accountId = pickExchangeAccountId(row);
  const kind = String(row.kind || row.type || '').trim().toLowerCase();
  const txid = stablePart(row.txid ?? row.txId ?? '');
  const ts = Number(row.timestamp ?? row.ts ?? 0);
  const tsNorm = Number.isFinite(ts) ? (ts > 1e12 ? Math.floor(ts / 1000) : Math.floor(ts)) : '';
  const side = String(row.side || '').trim().toUpperCase();
  const base = String(row.base || row.baseAsset || row.asset || row.token || '')
    .trim()
    .toUpperCase();
  const quote = String(row.quote || row.quoteAsset || '')
    .trim()
    .toUpperCase();
  const amount = stablePart(row.amount ?? row.baseAmount);
  const quoteAmount = stablePart(row.quoteAmount ?? row.quote_amount);
  const price = stablePart(row.price);
  const fee = stablePart(row.fee ?? row.feeAmount);
  const feeAsset = String(row.feeAsset || row.fee_asset || '')
    .trim()
    .toUpperCase();
  const importSource = stablePart(row.importSource || row.import_source || '');
  const importUid = stablePart(row.importUid || row.import_uid || '');

  return [
    vendor,
    accountId,
    kind,
    txid,
    tsNorm,
    side,
    base,
    quote,
    amount,
    quoteAmount,
    price,
    fee,
    feeAsset,
    importSource,
    importUid,
  ].join('|');
}

/**
 * Stable import identity:
 * <vendor>|<account>|<sourceFile>|<sourceRow>|<type>|<nativeReference>
 */
export function buildExchangeImportUid(row = {}, extras = {}) {
  const vendor = pickExchangeVendor(row);
  const accountId = pickExchangeAccountId(row);
  const sourceFile = stablePart(
    extras.sourceFile ?? row.sourceFile ?? row.source_file ?? row.file ?? ''
  );
  const sourceRow = stablePart(
    extras.sourceRow ?? row.row ?? row.sourceRow ?? row.source_row ?? ''
  );
  const type = String(extras.type ?? row.kind ?? row.type ?? '')
    .trim()
    .toLowerCase();
  const nativeReference = stablePart(
    extras.nativeReference ??
      row.txid ??
      row.txId ??
      row.orderId ??
      row.order_id ??
      row.transferId ??
      row.transfer_id ??
      row.refId ??
      row.refid ??
      row.remark ??
      `${row.timestamp ?? row.ts ?? ''}`
  );

  return `${vendor}|${accountId}|${sourceFile}|${sourceRow}|${type}|${nativeReference}`;
}

/** Scope key for per-account fallbacks: <exchange>|<accountId> */
export function buildExchangeAccountScopeKey(row = {}) {
  return `${pickExchangeVendor(row)}|${pickExchangeAccountId(row)}`;
}
